import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import SectionPicker from '../../reusable/SectionPicker/SectionPicker';
import { useShellAsideControl } from '../../layout/ShellAside';
import { SECTIONS } from './data';
import { t } from '../../i18n/i18n';
import './ProfileAside.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/** The rule NavLink applies, so the fill can't disagree with which row is lit. */
const isCurrent = (section, pathname) =>
  section.end
    ? pathname === section.to
    : pathname === section.to || pathname.startsWith(`${section.to}/`);

/**
 * The Profile section nav, drawn as the Affiliate aside is (1:16694): flush
 * against the rail at full height, with the top bar starting after it. That is
 * why it is published onto the shell rather than rendered inside the page —
 * see `useShellAside` — and why `/profile/*` joins the rail routes.
 *
 * No promo card down here: the Affiliate one is that programme's own marketing,
 * not part of the aside's chrome.
 */
export default function ProfileAside() {
  const { pathname } = useLocation();
  const { isAsideCollapsed, toggleAside } = useShellAsideControl();
  const [tip, setTip] = useState(null);

  const isRail = Boolean(isAsideCollapsed);

  useEffect(() => {
    if (!isRail) setTip(null);
  }, [isRail]);

  const peek = (event) => {
    if (!isRail) return;
    const row = event.target.closest?.('.profile-aside__link');
    const label = row?.dataset?.label;
    if (!label) return;

    const rect = row.getBoundingClientRect();
    setTip({ label, x: rect.right + 10, y: rect.top + rect.height / 2 });
  };

  /* One fill that travels between rows rather than each row painting its own,
     so moving between sections slides the highlight smoothly instead of cutting it,
     matching the main sidebar and the affiliate aside. */
  const index = SECTIONS.findIndex((section) => isCurrent(section, pathname));

  /* Where it rests if a route under /profile ever falls outside this list:
     it fades where it stands instead of sliding back to the first row. */
  const [resting, setResting] = useState(index < 0 ? 0 : index);

  useEffect(() => {
    if (index >= 0) setResting(index);
  }, [index]);

  const at = index < 0 ? resting : index;

  return (
    <aside className={`profile-aside shell__aside${isRail ? ' profile-aside--rail' : ''}`}>
      <div className="profile-aside__head">
        <div className="profile-aside__head-copy">
          <h2 className="profile-aside__title">{t('Profile')}</h2>
          <p className="profile-aside__subtitle">{t('Account settings')}</p>
        </div>
        <button
          type="button"
          className="profile-aside__collapse"
          onClick={toggleAside}
          aria-expanded={!isRail}
          aria-label={isRail ? t('Expand profile menu') : t('Collapse profile menu')}
          title={isRail ? t('Expand profile menu') : t('Collapse profile menu')}
        >
          <span className="icon" style={icon('sidebar-collapse')} />
        </button>
      </div>

      {/* Five rows don't fit across a phone, so the same five become a picker.
          Rendered alongside the list rather than swapped in at a JS breakpoint,
          so only CSS decides which one is live. */}
      <SectionPicker
        sections={SECTIONS.map((section) => ({ ...section, label: t(section.label) }))}
        label={t('Profile section')}
      />

      <nav
        className="profile-aside__nav"
        onMouseOver={peek}
        onMouseOut={() => setTip(null)}
        onFocus={peek}
        onBlur={() => setTip(null)}
        onScroll={() => setTip(null)}
      >
        {/* Inside the list, so it scrolls with the rows it marks. */}
        <span
          className={`profile-aside__fill${index >= 0 ? ' is-on' : ''}`}
          style={{ '--fill-i': at }}
          aria-hidden="true"
        />

        {SECTIONS.map((section) => (
          <NavLink
            key={section.to}
            to={section.to}
            end={section.end}
            data-label={t(section.label)}
            title={isRail ? t(section.label) : undefined}
            className={({ isActive }) => `profile-aside__link${isActive ? ' is-active' : ''}`}
          >
            <span className="icon profile-aside__icon" style={icon(section.icon)} />
            <span className="profile-aside__label">{t(section.label)}</span>
          </NavLink>
        ))}
      </nav>

      {isRail && tip &&
        createPortal(
          <div
            className="rail-tip"
            style={{ top: `${tip.y}px`, left: `${tip.x}px` }}
            aria-hidden="true"
          >
            {tip.label}
          </div>,
          document.body,
        )}
    </aside>
  );
}
